import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button, ListGroup, Spinner, Alert, Badge, Form, InputGroup } from "react-bootstrap";
import { FiFolder, FiTrash2, FiRefreshCw, FiPlus } from "react-icons/fi";
import { getHost } from "../utils/utils";

const getToken = () => localStorage.getItem("jwt");

const MonitorCategoriesModal = ({ show, onHide, monitor }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [newCategory, setNewCategory] = useState("");
  const [message, setMessage] = useState("");

  const endpoint = monitor?._id ? `${getHost()}/api/admin/monitors/${monitor._id}/categories` : null;

  const fetchCategories = useCallback(async () => {
    if (!endpoint) return;
    setLoading(true);
    setMessage("");
    try {
      const res = await fetch(endpoint, { headers: { Authorization: `Bearer ${getToken()}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Request failed");
      setCategories(data.categories || []);
    } catch (error) {
      setMessage(`Failed to load categories: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => {
    if (show && endpoint) {
      setNewCategory("");
      fetchCategories();
    }
  }, [show, endpoint, fetchCategories]);

  const add = async (event) => {
    event.preventDefault();
    const category = newCategory.trim();
    if (!category || !endpoint) return;
    setAdding(true);
    setMessage("");
    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" },
        body: JSON.stringify({ category }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Request failed");
      setCategories(data.categories || []);
      setNewCategory("");
      setMessage(`Added ${category}`);
    } catch (error) {
      setMessage(`Failed to add ${category}: ${error.message}`);
    } finally {
      setAdding(false);
    }
  };

  const remove = async (category) => {
    if (!endpoint) return;
    if (!window.confirm(`Remove ${category}? The monitor stops discovering products from it.`)) return;
    setRemoving(category);
    setMessage("");
    try {
      const res = await fetch(`${endpoint}?category=${encodeURIComponent(category)}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Request failed");
      setCategories(data.categories || []);
      setMessage(`Removed ${category}`);
    } catch (error) {
      setMessage(`Failed to remove ${category}: ${error.message}`);
    } finally {
      setRemoving(null);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg" contentClassName="bg-dark text-light">
      <Modal.Header closeButton closeVariant="white" className="border-secondary">
        <Modal.Title className="d-flex align-items-center gap-2 min-w-0">
          <FiFolder className="flex-shrink-0" />
          Categories
          {monitor?.name && <Badge bg="secondary" className="ms-2 text-truncate" style={{ maxWidth: "40vw" }}>{monitor.name}</Badge>}
          {!loading && <Badge bg="info" className="ms-1">{categories.length}</Badge>}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className="text-muted small mb-3">
          Category URLs, paths or search terms this monitor crawls to discover products. Only monitors that run on categories use them, and changes reach the monitor within a few minutes. Adding a category can ping the products found in it.
        </p>

        {message && (
          <Alert variant={message.startsWith("Failed") ? "danger" : "success"} className="py-2 px-3 small">
            {message}
          </Alert>
        )}

        <Form onSubmit={add} className="mb-3">
          <InputGroup size="sm">
            <Form.Control
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              placeholder="/collections/pokemon, https://…, or a search term"
              className="bg-dark text-light border-secondary"
              disabled={adding}
            />
            <Button type="submit" variant="outline-success" disabled={adding || !newCategory.trim()}>
              {adding ? <Spinner size="sm" /> : <><FiPlus /> Add</>}
            </Button>
            <Button variant="outline-secondary" onClick={fetchCategories} disabled={loading}>
              <FiRefreshCw className={loading ? "spin" : ""} />
            </Button>
          </InputGroup>
        </Form>

        {loading ? (
          <div className="text-center py-4"><Spinner size="sm" /> Loading…</div>
        ) : categories.length === 0 ? (
          <div className="text-muted text-center py-4">No categories on this monitor.</div>
        ) : (
          <ListGroup variant="flush" style={{ maxHeight: 400, overflowY: "auto" }}>
            {categories.map((category) => (
              <ListGroup.Item
                key={category}
                className="bg-transparent text-light border-secondary d-flex align-items-center justify-content-between gap-2"
              >
                <code className="text-info text-break min-w-0">{category}</code>
                <Button
                  size="sm"
                  variant="outline-danger"
                  className="flex-shrink-0"
                  onClick={() => remove(category)}
                  disabled={removing === category}
                >
                  {removing === category ? <Spinner size="sm" /> : <><FiTrash2 /> Remove</>}
                </Button>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Modal.Body>

      <Modal.Footer className="border-secondary">
        <Button variant="secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MonitorCategoriesModal;
